import { useState, useEffect, useCallback } from 'react';
import './PrtotypeExplainer.css';

const STORAGE_KEY = 'prtotype-explainer-dismissed';

export default function PrtotypeExplainer() {
    const [open, setOpen] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        let dismissed = null;
        try { dismissed = localStorage.getItem(STORAGE_KEY); }
        catch (e) { console.warn('PrtotypeExplainer: localStorage unavailable', e); }
        if (!dismissed) {
            const t = setTimeout(() => setOpen(true), 900);
            return () => clearTimeout(t);
        }
    }, []);

    const close = useCallback(() => {
        setOpen(false);
        try { localStorage.setItem(STORAGE_KEY, '1'); }
        catch (e) { console.warn('PrtotypeExplainer: localStorage set failed', e); }
    }, []);

    const reopen = useCallback(() => setOpen(true), []);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === 'Escape') close();
            // minimal focus trap — keep tab inside modal
            if (e.key === 'Tab') {
                const modal = document.querySelector('.prto-modal');
                if (!modal) return;
                const focusables = modal.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
                if (focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
                else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
            }
        };
        document.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        // autofocus close button for trap start
        setTimeout(() => document.querySelector('.prto-close')?.focus(), 0);
        const prevActive = document.activeElement;
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
            if (prevActive instanceof HTMLElement) prevActive.focus();
        };
    }, [open, close]);

    if (!mounted) return null;

    return (
        <>
            {/* Persistent luxury badge — reopens the explainer */}
            <button
                type="button"
                className="prto-badge"
                onClick={reopen}
                aria-label="About PRTOTYPE.COM — open explainer"
                aria-expanded={open}
                aria-haspopup="dialog"
            >
                <span className="prto-badge__mark" aria-hidden="true">PR</span>
                <span className="prto-badge__o" aria-hidden="true">O</span>
                <span className="prto-badge__rest" aria-hidden="true">TOTYPE</span>
                <span className="prto-badge__dot">.COM</span>
                <span className="prto-badge__sep" aria-hidden="true">—</span>
                <span className="prto-badge__hint">A production studio</span>
            </button>

            {open && (
                <div
                    className="prto-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="prto-title"
                    onClick={close}
                >
                    <div className="prto-glow" aria-hidden="true" />
                    <div
                        className="prto-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="prto-close"
                            onClick={close}
                            aria-label="Close explainer"
                        >
                            ×
                        </button>

                        <div className="prto-eyebrow">
                            <span className="prto-eyebrow__kicker">PRTOTYPE.COM</span>
                            <span className="prto-eyebrow__sep">·</span>
                            <span>Production studio</span>
                        </div>

                        <h2 id="prto-title" className="prto-title">
                            This is a
                            <br />
                            <em>PRTOTYPE.COM</em> site.
                        </h2>

                        <p className="prto-annotation">
                            The missing <span className="prto-o">O</span> is an annotation, not a typo.
                        </p>

                        <div className="prto-rule" aria-hidden="true" />

                        <p className="prto-body">
                            <strong>Sheeran Looper</strong> is a PRTOTYPE production — a browser-based
                            control surface for the Ed Sheeran Looper Plus, built to take a
                            vibe-coded idea to production.
                        </p>

                        <p className="prto-body prto-body--muted">
                            We take stalled AI pilots, fragile spreadsheets and half-shipped
                            demos and ship them properly: fixed price, written spec, instrumented,
                            and owned by you. No theatre, just production.
                        </p>

                        <ul className="prto-list">
                            <li><span>Specs before code —</span> what “done” means, in writing</li>
                            <li><span>Fixed prices —</span> no hourly drift</li>
                            <li><span>Yours to keep —</span> on your infra, under your name</li>
                        </ul>

                        <div className="prto-ctas">
                            <a
                                className="prto-cta prto-cta--primary"
                                href="https://prtotype.com"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Visit PRTOTYPE.COM
                                <span aria-hidden="true" className="prto-cta__arrow">→</span>
                            </a>
                            <button
                                type="button"
                                className="prto-cta prto-cta--ghost"
                                onClick={close}
                            >
                                Explore the looper
                            </button>
                        </div>

                        <div className="prto-foot">
                            <span>sheeranlooper.prtotype.com</span>
                            <span className="prto-foot__sep">·</span>
                            <a href="https://prtotype.com" target="_blank" rel="noopener noreferrer">prtotype.com</a>
                            <span className="prto-foot__sep">·</span>
                            <a href="mailto:info@prtotype.com">info@prtotype.com</a>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
